/*
 * Design: Gradient Noir – Blog listing page
 * Featured article hero → category filters → article grid
 */
import { useState, useMemo } from "react";
import { Link } from "wouter";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { categories, formatDate } from "@/data/blogPosts";
import { useApiData } from "@/hooks/useApiData";
import type { BlogPost } from "@shared/types";
import { Clock, ArrowLeft, Search, BookOpen } from "lucide-react";

function AnimatedSection({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const { ref, isVisible } = useScrollAnimation(0.1);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(30px)",
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

function BlogCard({ post, index }: { post: BlogPost; index: number }) {
  return (
    <AnimatedSection delay={index * 80}>
      <Link
        href={`/blog/${post.slug}`}
        className="block glass-card overflow-hidden group hover:-translate-y-1.5 transition-all duration-300 hover:border-[#66CDAA]/20 hover:shadow-[0_16px_48px_rgba(102,205,170,0.1)] h-full"
      >
        <div className="h-48 overflow-hidden relative">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/60 to-transparent" />
          <div className="absolute bottom-3 right-3">
            <span className="px-2.5 py-1 rounded-full bg-[#66CDAA]/15 border border-[#66CDAA]/25 text-[11px] text-[#66CDAA] font-semibold backdrop-blur-sm">
              {categories.find((c) => c.id === post.category)?.label}
            </span>
          </div>
        </div>
        <div className="p-6">
          <div className="flex items-center gap-3 text-xs text-white/40 mb-3">
            <span>{formatDate(post.date)}</span>
            <span className="w-1 h-1 rounded-full bg-white/20" />
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {post.readTime} דק׳ קריאה
            </span>
          </div>
          <h3 className="text-lg font-bold text-white mb-2 font-[Rubik] leading-snug group-hover:text-[#66CDAA] transition-colors line-clamp-2">
            {post.title}
          </h3>
          <p className="text-sm text-white/45 leading-relaxed line-clamp-2 mb-4">
            {post.excerpt}
          </p>
          <span className="inline-flex items-center gap-1.5 text-sm text-[#66CDAA] font-medium opacity-0 group-hover:opacity-100 transition-opacity">
            קראו עוד
            <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" />
          </span>
        </div>
      </Link>
    </AnimatedSection>
  );
}

export default function Blog() {
  const { data: blogPosts } = useApiData<BlogPost[]>("/blog-posts");
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const allPosts = blogPosts || [];
  const featured = allPosts.find((p) => p.featured) || allPosts[0];

  const filteredPosts = useMemo(() => {
    let posts = activeCategory === "all"
      ? allPosts
      : allPosts.filter((p) => p.category === activeCategory);

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      posts = posts.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.excerpt.toLowerCase().includes(q)
      );
    }

    return posts;
  }, [activeCategory, searchQuery, allPosts]);

  return (
    <div>
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 gradient-mesh-strong" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-[#66CDAA]/10 rounded-full blur-[120px]" />
        <div className="container relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#66CDAA]/10 border border-[#66CDAA]/20 mb-6">
              <BookOpen className="w-4 h-4 text-[#66CDAA]" />
              <span className="text-sm text-[#66CDAA] font-medium">
                הבלוג של אפסייט
              </span>
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 font-[Rubik] leading-tight">
              תובנות, טיפים ו<span className="gradient-text">ידע מקצועי</span>
            </h1>
            <p className="text-lg text-white/60 leading-relaxed max-w-2xl">
              מאמרים מקצועיים על בניית אתרים, קידום אורגני, שיווק דיגיטלי ו-AI
              – כל מה שצריך כדי לצמוח באונליין.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Article */}
      {featured && <section className="py-12 lg:py-16 section-navy">
        <div className="container">
          <AnimatedSection>
            <Link
              href={`/blog/${featured.slug}`}
              className="block glass-card overflow-hidden group hover:border-[#66CDAA]/25 transition-all duration-300 hover:shadow-[0_20px_60px_rgba(102,205,170,0.12)]"
            >
              <div className="grid lg:grid-cols-2">
                <div className="h-64 lg:h-auto overflow-hidden relative">
                  <img
                    src={featured.image}
                    alt={featured.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-l from-[#0F172A]/40 to-transparent lg:bg-gradient-to-r" />
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1.5 rounded-full bg-[#66CDAA] text-[#0F172A] text-xs font-bold">
                      מאמר מומלץ
                    </span>
                  </div>
                </div>
                <div className="p-8 lg:p-10 flex flex-col justify-center">
                  <div className="flex items-center gap-3 text-sm text-white/40 mb-4">
                    <span className="px-2.5 py-1 rounded-full bg-[#66CDAA]/10 border border-[#66CDAA]/20 text-[11px] text-[#66CDAA] font-semibold">
                      {categories.find((c) => c.id === featured.category)?.label}
                    </span>
                    <span>{formatDate(featured.date)}</span>
                    <span className="w-1 h-1 rounded-full bg-white/20" />
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {featured.readTime} דק׳
                    </span>
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4 font-[Rubik] leading-snug group-hover:text-[#66CDAA] transition-colors">
                    {featured.title}
                  </h2>
                  <p className="text-white/50 leading-relaxed mb-6 line-clamp-3">
                    {featured.excerpt}
                  </p>
                  <span className="inline-flex items-center gap-2 text-[#66CDAA] font-medium">
                    קראו את המאמר המלא
                    <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                  </span>
                </div>
              </div>
            </Link>
          </AnimatedSection>
        </div>
      </section>}

      {/* Filters + Search */}
      <section className="py-8 section-navy border-t border-white/5">
        <div className="container">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            {/* Category Filters */}
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                    activeCategory === cat.id
                      ? "bg-[#66CDAA] text-[#0F172A] shadow-[0_4px_20px_rgba(102,205,170,0.3)]"
                      : "bg-white/5 text-white/50 hover:text-white hover:bg-white/10 border border-white/5"
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative w-full md:w-72">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="חיפוש מאמרים..."
                className="w-full bg-white/5 border border-white/10 rounded-xl pr-10 pl-4 py-2.5 text-sm text-white placeholder-white/30 focus:border-[#66CDAA]/40 focus:ring-1 focus:ring-[#66CDAA]/20 outline-none transition-all"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-12 lg:py-20 section-dark relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-l from-transparent via-[#66CDAA]/20 to-transparent" />
        <div className="absolute bottom-1/3 left-0 w-80 h-80 bg-[#66CDAA]/5 rounded-full blur-[100px]" />
        <div className="container relative z-10">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-20">
              <BookOpen className="w-16 h-16 text-white/10 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white/40 font-[Rubik] mb-2">
                לא נמצאו מאמרים
              </h3>
              <p className="text-white/30">
                נסו לשנות את הקטגוריה או את מילת החיפוש
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPosts.map((post, i) => (
                <BlogCard key={post.slug} post={post} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0 gradient-mesh-strong" />
        <div className="container relative z-10 text-center">
          <AnimatedSection>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6 font-[Rubik]">
              רוצים לשמוע עוד?
            </h2>
            <p className="text-lg text-white/60 mb-8 max-w-xl mx-auto">
              צרו קשר ונשמח לשתף אתכם בידע ובניסיון שלנו.
            </p>
            <Link
              href="/contact"
              className="glow-btn inline-block text-lg px-10 py-4"
            >
              דברו איתנו
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
